import * as React from 'react'
import clsx from 'clsx'
import SemanticSuccess from '../internal/svg-icons/SemanticSuccess'
import SemanticWarning from '../internal/svg-icons/SemanticWarning'
import { SvgIcon } from '../SvgIcon'
import { SxProps, sx as sxFunc } from '../sx'
import style from './index.module.scss'

export interface StepIconClasses {
  /** Styles applied to the root element. */
  root: string
  /** Styles applied to the SVG text element. */
  text: string
  /** State class applied to the root element if `active={true}`. */
  active: string
  /** State class applied to the root element if `completed={true}`. */
  completed: string
  /** State class applied to the root element if `error={true}`. */
  error: string
}

export interface StepIconProps
  extends Omit<React.HTMLAttributes<HTMLOrSVGElement>, 'color'> {
  /**
   * Whether this step is active.
   * @default false
   */
  active?: boolean
  /**
   * Override or extend the styles applied to the component.
   */
  classes?: Partial<StepIconClasses>
  /**
   * Mark the step as completed. Is passed to child components.
   * @default false
   */
  completed?: boolean
  /**
   * If `true`, the step is marked as failed.
   * @default false
   */
  error?: boolean
  /**
   * The label displayed in the step icon.
   */
  icon: React.ReactNode
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   */
  sx?: SxProps
}

export const StepIcon = React.forwardRef<HTMLOrSVGElement, StepIconProps>(
  // @ts-ignore
  (props, ref) => {
    const {
      classes,
      className: classNameProp,
      active = false,
      completed = false,
      error = false,
      icon,
      sx,
      ...other
    } = props

    if (typeof icon === 'number' || typeof icon === 'string') {
      const className = clsx(
        classNameProp,
        style.root,
        classes?.root,
        active && [style.active, classes?.active],
        completed && [style.completed, classes?.completed],
        error && [style.error, classes?.error],
        sxFunc(sx)
      )

      if (error) {
        return (
          <SemanticWarning
            className={clsx(
              style.root,
              classes?.root,
              style.error,
              classes?.error,
              classNameProp
            )}
            ref={ref}
            sx={sx}
            {...other}
          />
        )
      }
      if (completed) {
        return (
          <SemanticSuccess
            className={clsx(
              style.root,
              classes?.root,
              classes?.completed,
              style.completed,
              classNameProp
            )}
            ref={ref}
            sx={sx}
            {...other}
          />
        )
      }
      return (
        <SvgIcon className={className} ref={ref} sx={sx} {...other}>
          <circle cx="12" cy="12" r="10" />
          <text
            x="12"
            y="12"
            textAnchor="middle"
            dominantBaseline="central"
            className={(classes?.text, style.text)}
          >
            {icon}
          </text>
        </SvgIcon>
      )
    }
    return icon
  }
)

StepIcon.displayName = 'StepIcon'
